<script lang="ts" setup>
import LabelText from '~/components/form-elements/LabelText.vue'
import Mask from '~/enums/mask'

const maskaOptions = {
  phoneNumber: Mask.getMaskaOptions(Mask.PhoneNumber),
  phoneNumberRu: Mask.getMaskaOptions(Mask.PhoneNumberRu)
}

const props = withDefaults(
  defineProps<{
    defaultCountry?: string
    isOnlyOneCountry?: boolean
    isRequired?: boolean
    labelText?: string
    modelValue: string | null
  }>(),
  {
    defaultCountry: 'ru',
    isOnlyOneCountry: false,
    isRequired: false,
    labelText: 'Номер телефона'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', x: string): void
}>()

const isChanged = ref(false)
const isValid = ref(true)
const country = ref(props.defaultCountry)

const maskaOptionsForPhoneNumber = computed(() => {
  const { phoneNumber, phoneNumberRu } = maskaOptions

  return isCountryRu.value ? phoneNumberRu : phoneNumber
})
const value = computed({
  get (): string | null {
    return props.modelValue
  },
  set (x: string): void {
    const isReset = !x.length

    if (isReset) {
      isValid.value = true
    } else if (!(x === '+' || x === '+7')) {
      isValid.value = validate(x)
    }

    emit('update:modelValue', x)
  }
})
const mask = computed(() => maskaOptionsForPhoneNumber.value.mask)
const maskPrefix = computed(() => mask.value.replaceAll('#', ''))
const isCountryRu = computed(() => country.value === 'ru')

watch(
  country,
  () => {
    value.value = isCountryRu.value ? '+7' : '+'
    isValid.value = false
  },
  { immediate: true }
)

function validate (x: string): boolean {
  return mask.value.length === x.length
}

function numberCount (): number {
  const code = mask.value.replace(/\D/g, '')

  return mask.value.replace(/[^#]/g, '').length + code.length
}

defineExpose({ props })
</script>

<template>
  <div>
    <LabelText class="mb-1" :is-required="isRequired" :value="labelText" />

    <div class="phone-number-input-group" :class="{ 'not-valid': !isValid && isChanged }">
      <div class="country-container absolute flex items-center">
        <span v-if="isOnlyOneCountry">🇷🇺</span>
        <span v-else>
          {{ isCountryRu ? '🇷🇺' : '🌎' }}
        </span>
        <DropdownMenu v-if="!isOnlyOneCountry" icon-name="outlined/expand-more" position="left">
          <DropdownMenuItem class="whitespace-nowrap" @click="() => country = 'ru'">
            <span>🇷🇺 Россия</span>
          </DropdownMenuItem>
          <DropdownMenuItem class="whitespace-nowrap" @click="() => country = 'other'">
            <span>🌎 Другая страна</span>
          </DropdownMenuItem>
        </DropdownMenu>
      </div>

      <input
        v-model="value"
        v-maska="maskaOptionsForPhoneNumber"
        class="bg-white"
        type="text"
        @change="isChanged = true"
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

@use 'assets/css/variables';

.phone-number-input-group {
  $iconSize: 24px;

  position: relative;

  input {
    appearance: none;
    border: 1px solid color.mix(variables.$main-gray-color, white, 60%);
    border-radius: 12px;
    box-sizing: border-box;
    font-size: 16px;
    line-height: 24px;
    outline: none;
    padding: 11px 16px 11px calc(11px + $iconSize + 32px);
    transition: border .25s ease-in-out;
    width: 100%;

    &:focus,
    &:hover {
      border-color: variables.$main-green-color;
    }
  }

  &.not-valid {
    input {
      border: 1px solid color.mix(variables.$main-red-color, white, 60%);
    }
  }

  .country-container {
    // border-right: 1px solid variables.$main-green-color;
    font-size: $iconSize;
    height: 100%;
    left: 16px;
    padding-right: 16px;
  }
}
</style>
